/** Conversational new-ticket capture for the terminal assistant. */
import { todayISO, fmtDate, esc, fmtAmt, ticketPricingFromExtra } from './utils.js';
import { formatPassengerSummary } from './assistant-lead-format.js';
import { prepareFlowSteps } from './terminal-flow-merge.js';
import { injectCustomSteps } from './terminal-flow-custom.js';
import { mergeCustomFieldsIntoExtra } from './terminal-flow-handlers.js';
import {
  initNewTicketDataV2,
  activeNewTicketStepsV2,
  buildNewTicketLeadBodyV2,
  formatNewTicketSummaryV2,
} from './assistant-ticket-v2.js';

const TZ = 'Asia/Colombo';

export function currentYearPrefix() {
  return todayISO().slice(0, 2);
}

export function capitalizeName(raw) {
  return String(raw || '')
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function upperDoc(raw) {
  return String(raw || '').replace(/\s+/g, '').toUpperCase();
}

export function initNewTicketData() {
  return initNewTicketDataV2();
}

export function isYes(text) {
  const s = String(text || '').trim().toLowerCase();
  return s === 'y' || s === 'yes';
}

export function isNo(text) {
  const s = String(text || '').trim().toLowerCase();
  return s === 'n' || s === 'no';
}

export function isSkip(text) {
  const s = String(text || '').trim().toLowerCase();
  return !s || s === 'skip' || s === '-' || s === 'none' || s === 'n/a';
}

export function parseSmartTime(raw) {
  let s = String(raw || '').trim();
  if (!s) return null;
  if (/^\d{3,4}$/.test(s)) {
    s = s.padStart(4, '0');
    s = `${s.slice(0, 2)}:${s.slice(2)}`;
  }
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** @deprecated use parseSmartTime */
export function parseTime24(raw) {
  return parseSmartTime(raw);
}

export function parseSmartDate(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (!s || s === 'skip' || s === '-') return null;
  if (s === 'today') return todayISO();
  if (s === 'tomorrow') {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString('en-CA', { timeZone: TZ });
  }
  const rel = s.match(/^\+(\d+)\s*(day|days|d)?$/);
  if (rel) {
    const d = new Date();
    d.setDate(d.getDate() + Number(rel[1]));
    return d.toLocaleDateString('en-CA', { timeZone: TZ });
  }
  const compact = s.replace(/\s+/g, '');
  if (/^\d{6}$/.test(compact)) {
    const yy = compact.slice(0, 2);
    const mm = compact.slice(2, 4);
    const dd = compact.slice(4, 6);
    const year = `${currentYearPrefix()}${yy}`;
    const iso = `${year}-${mm}-${dd}`;
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
    return null;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const combo = s.match(/^(\d{6})\s+(\d{3,4})$/);
  if (combo) {
    return parseSmartDate(combo[1]);
  }
  return null;
}

export function parsePaxType(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (/^adt|adult/.test(s)) return 'adt';
  if (/^stu|student/.test(s)) return 'stu';
  if (/^chd|child/.test(s)) return 'chd';
  if (/^inf|infant/.test(s)) return 'inf';
  return null;
}

export function parseTripType(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (/^(1|one[\s-]?way|ow|1\s*way|single|o)$/.test(s)) return 'one_way';
  if (/^(2|return|round[\s-]?trip|rt|2\s*way|r)$/.test(s)) return 'return';
  return null;
}

/** Coerce stored trip type (incl. numbered replies) to a DB-safe value. */
export function normalizeTripType(val) {
  if (val == null || val === '') return null;
  if (val === 'one_way' || val === 'return' || val === 'multi') return val;
  return parseTripType(val);
}

function normalizePaxCounts(pax = {}) {
  return {
    adt: Number(pax.adt) || 0,
    stu: Number(pax.stu) || 0,
    chd: Number(pax.chd) || 0,
    inf: Number(pax.inf) || 0,
  };
}

function isSimpleRouting(data) {
  const t = data.preferences?.transit;
  return t === 'direct' || t === 'transit' || t === 'ok' || t === 'preferred';
}

function applyGlobalTripType(data, tripType) {
  if (!tripType) return;
  data.trip_type = tripType;
  for (const kind of ['adt', 'stu']) {
    (data.travelers?.[kind] || []).forEach((pr) => {
      if (pr._sameItineraryAsFirst !== false) pr.trip_type = tripType;
    });
  }
}

export function parseBaggage(raw) {
  if (isSkip(raw)) return null;
  const s = String(raw || '').trim().toLowerCase();
  if (s === '1' || s.includes('cabin')) return 'cabin';
  if (s === '2' || s.includes('23')) return '23kg';
  if (s === '3' || s.includes('30')) return '30kg';
  if (s === '4' || s.includes('extra')) return 'extra';
  return null;
}

export function parseSeat(raw) {
  if (isSkip(raw)) return null;
  const s = String(raw || '').trim().toLowerCase();
  if (s.startsWith('w') || s === '1') return 'window';
  if (s.startsWith('a') || s === '2') return 'aisle';
  if (s.startsWith('m') || s === '3') return 'middle';
  if (s.includes('exit') || s === '4') return 'exit';
  if (s.includes('front') || s === '5') return 'front';
  return null;
}

export function parseTransit(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (/^(1|direct|d|nonstop|non-stop)$/.test(s)) return 'direct';
  if (/^(2|transit|ok|any|t)$/.test(s)) return 'transit';
  if (/^(3|multi|multi-city|multi_city|multicity|m)$/.test(s)) return 'multi';
  // Legacy answers
  if (/^(via|preferred|pref|v)$/.test(s)) return 'transit';
  return null;
}

/** Parse space-separated IATA chain e.g. CMB SIN KUL CMB → flight segments. */
export function parseMultiCityRoute(raw) {
  const codes = String(raw || '')
    .trim()
    .toUpperCase()
    .split(/[\s,→\-–/]+/)
    .map((c) => c.trim())
    .filter(Boolean);
  if (codes.length < 3) return null;
  if (!codes.every((c) => /^[A-Z]{3}$/.test(c))) return null;
  const segments = [];
  for (let i = 0; i < codes.length - 1; i += 1) {
    segments.push({
      origin: codes[i],
      destination: codes[i + 1],
      depart_date: '',
      depart_time: '',
      arrive_time: '',
    });
  }
  return { airports: codes, segments, route: codes.join(' ') };
}

export function parseClass(raw) {
  if (isSkip(raw)) return null;
  const s = String(raw || '').trim().toLowerCase();
  if (s.startsWith('econ')) return 'Economy';
  if (s.startsWith('prem')) return 'Premium Economy';
  if (s.startsWith('bus')) return 'Business';
  if (s.startsWith('first') || s === 'f') return 'First';
  return raw.trim();
}

export function parseGender(raw) {
  const s = String(raw || '').trim().toUpperCase();
  if (s === 'M' || s === 'MALE') return 'Male';
  if (s === 'F' || s === 'FEMALE') return 'Female';
  return null;
}

export function travelerLabel(kind, index, profile = {}) {
  const name = String(profile?.name || '').trim();
  const tag = kind === 'stu' ? 'STU' : 'ADT';
  const base = `${tag} ${index + 1}`;
  const g = profile?.gender === 'Male' ? 'M' : profile?.gender === 'Female' ? 'F' : '';
  if (name) return name;
  if (g) return `${base} (${g})`;
  return base;
}

function ensureTravelers(data) {
  const adt = data.pax?.adt || 0;
  const stu = data.pax?.stu || 0;
  while (data.travelers.adt.length < adt) {
    data.travelers.adt.push(defaultProfile('adt', data.travelers.adt.length));
  }
  data.travelers.adt.length = adt;
  while (data.travelers.stu.length < stu) {
    data.travelers.stu.push(defaultProfile('stu', data.travelers.stu.length));
  }
  data.travelers.stu.length = stu;
  if (data.trip_type && !isMultiCityRouting(data)) {
    applyGlobalTripType(data, data.trip_type);
  }
}

function defaultProfile(kind, index) {
  return {
    kind,
    index,
    gender: null,
    name: null,
    trip_type: 'one_way',
    origin: '',
    destination: '',
    depart_date: '',
    return_date: '',
    depart_time: '',
    arrive_time: '',
    transit_stop: null,
    transit_layover: null,
    cost: null,
    profit: null,
    fare_confirmed: false,
  };
}

function getProfile(data, kind, index) {
  ensureTravelers(data);
  return data.travelers[kind][index];
}

function firstReferenceProfile(data) {
  if (data.pax?.adt >= 1 && data.travelers.adt[0]) return data.travelers.adt[0];
  if (data.pax?.stu >= 1 && data.travelers.stu[0]) return data.travelers.stu[0];
  return data.travelers.adt[0] || data.travelers.stu[0];
}

function referenceTravelerLabel(data) {
  const ref = firstReferenceProfile(data);
  if (!ref) return 'ADT 1';
  if (ref === data.travelers.adt[0]) return travelerLabel('adt', 0, ref);
  return travelerLabel('stu', 0, ref);
}

function copyItineraryFields(from, to) {
  to.trip_type = from.trip_type || 'one_way';
  to.origin = from.origin || '';
  to.destination = from.destination || '';
  to.depart_date = from.depart_date || '';
  to.return_date = from.return_date || '';
  to.depart_time = from.depart_time || '';
  to.arrive_time = from.arrive_time || '';
  to.transit_stop = from.transit_stop ?? null;
  to.transit_layover = from.transit_layover ?? null;
}

function copyFareFields(from, to) {
  to.cost = from.cost ?? null;
  to.profit = from.profit ?? null;
  to.fare_confirmed = !!(from.fare_confirmed || (from.cost != null && from.profit != null));
}

function shouldAskItineraryDetails(kind, index, profile) {
  return index === 0 || profile._sameItineraryAsFirst === false;
}

function shouldAskFareDetails(kind, index, profile) {
  return index === 0 || profile._sameFareAsFirst === false;
}

function transitAllowed(data) {
  return data.preferences?.transit === 'transit'
    || data.preferences?.transit === 'ok'
    || data.preferences?.transit === 'preferred';
}

function isMultiCityRouting(data) {
  return data.preferences?.transit === 'multi';
}

function shouldAskSimpleRoute(data) {
  return !isMultiCityRouting(data);
}

function profileSteps(data, kind, index) {
  const label = kind === 'stu' ? `STU ${index + 1}` : `ADT ${index + 1}`;
  const p = () => getProfile(data, kind, index);
  const steps = [
    {
      key: `traveler:${kind}:${index}:gender`,
      ask: `${label} — gender? (M / F)`,
      hint: 'M for Male · F for Female',
      validate: (v) => (!parseGender(v) ? 'Reply M or F.' : null),
      parse: (v) => parseGender(v),
      apply: (val) => { p().gender = val; },
    },
    {
      key: `traveler:${kind}:${index}:name`,
      ask: () => `${travelerLabel(kind, index, p())} — passenger name?`,
      hint: 'Full name as on passport.',
      validate: (v) => (!String(v).trim() ? 'Name is required.' : null),
      parse: (v) => capitalizeName(v),
      apply: (val) => { p().name = val; },
    },
    {
      key: `traveler:${kind}:${index}:same_itinerary`,
      when: () => index >= 1,
      ask: () => {
        const refLabel = referenceTravelerLabel(data);
        return `${travelerLabel(kind, index, p())} — same flight itinerary as ${refLabel}? (Y/N)`;
      },
      hint: 'Y copies route, dates, times, and transit from the first passenger.',
      validate: (v) => (isYes(v) || isNo(v) ? null : 'Reply Y or N.'),
      parse: (v) => isYes(v),
      apply: (val) => {
        const ref = firstReferenceProfile(data);
        if (val && ref) {
          copyItineraryFields(ref, p());
          p()._sameItineraryAsFirst = true;
        } else {
          p()._sameItineraryAsFirst = false;
          if (data.trip_type) p().trip_type = data.trip_type;
        }
      },
    },
    {
      key: `traveler:${kind}:${index}:origin`,
      ask: `${travelerLabel(kind, index, p())} — departure airport?`,
      hint: 'IATA code, e.g. CMB',
      when: () => shouldAskItineraryDetails(kind, index, p()) && shouldAskSimpleRoute(data),
      validate: (v) => (/^[A-Z]{3}$/.test(v.trim().toUpperCase()) ? null : 'Enter 3-letter airport code.'),
      parse: (v) => v.trim().toUpperCase(),
      apply: (val) => { p().origin = val; },
    },
    {
      key: `traveler:${kind}:${index}:destination`,
      ask: `${travelerLabel(kind, index, p())} — destination airport?`,
      hint: 'IATA code, e.g. DXB',
      when: () => shouldAskItineraryDetails(kind, index, p()) && shouldAskSimpleRoute(data),
      validate: (v) => (/^[A-Z]{3}$/.test(v.trim().toUpperCase()) ? null : 'Enter 3-letter airport code.'),
      parse: (v) => v.trim().toUpperCase(),
      apply: (val) => { p().destination = val; },
    },
    {
      key: `traveler:${kind}:${index}:depart_date`,
      ask: `${travelerLabel(kind, index, p())} — departure date?`,
      hint: 'YYMMDD or YYYY-MM-DD (e.g. 260723)',
      when: () => shouldAskItineraryDetails(kind, index, p()),
      validate: (v) => (!parseSmartDate(v) ? 'Invalid date. Use YYMMDD or YYYY-MM-DD.' : null),
      parse: (v) => parseSmartDate(v),
      apply: (val) => { p().depart_date = val; },
    },
    {
      key: `traveler:${kind}:${index}:return_date`,
      ask: `${travelerLabel(kind, index, p())} — return date?`,
      when: () => shouldAskItineraryDetails(kind, index, p()) && p().trip_type === 'return',
      validate: (v) => (!parseSmartDate(v) ? 'Invalid return date.' : null),
      parse: (v) => parseSmartDate(v),
      apply: (val) => { p().return_date = val; },
    },
    {
      key: `traveler:${kind}:${index}:depart_time`,
      ask: `${travelerLabel(kind, index, p())} — departure time?`,
      hint: 'HHMM or HH:MM (24h, max 23:59)',
      when: () => shouldAskItineraryDetails(kind, index, p()),
      validate: (v) => (!parseSmartTime(v) ? 'Invalid time. Use HHMM or HH:MM between 00:00–23:59.' : null),
      parse: (v) => parseSmartTime(v),
      apply: (val) => { p().depart_time = val; },
    },
    {
      key: `traveler:${kind}:${index}:arrive_time`,
      ask: `${travelerLabel(kind, index, p())} — arrival time?`,
      hint: 'HHMM or HH:MM (max 23:59)',
      when: () => shouldAskItineraryDetails(kind, index, p()),
      validate: (v) => (!parseSmartTime(v) ? 'Invalid time. Use HHMM or HH:MM between 00:00–23:59.' : null),
      parse: (v) => parseSmartTime(v),
      apply: (val) => { p().arrive_time = val; },
    },
    {
      key: `traveler:${kind}:${index}:transit_stop`,
      ask: `${travelerLabel(kind, index, p())} — transit stop airport?`,
      hint: 'IATA code or N for direct flight',
      when: () => shouldAskItineraryDetails(kind, index, p()) && transitAllowed(data),
      optional: true,
      validate: (v) => {
        if (isSkip(v) || isNo(v)) return null;
        if (/^[A-Z]{3}$/.test(v.trim().toUpperCase())) return null;
        return 'Enter 3-letter code or N for none.';
      },
      parse: (v) => {
        if (isSkip(v) || isNo(v)) return null;
        return v.trim().toUpperCase();
      },
      apply: (val) => { p().transit_stop = val; },
    },
    {
      key: `traveler:${kind}:${index}:transit_layover`,
      ask: `${travelerLabel(kind, index, p())} — transit/layover time?`,
      hint: 'HH:MM (24h) — time at transit airport',
      when: () => shouldAskItineraryDetails(kind, index, p()) && transitAllowed(data) && !!p().transit_stop,
      validate: (v) => (!parseSmartTime(v) ? 'Invalid layover time (00:00–23:59).' : null),
      parse: (v) => parseSmartTime(v),
      apply: (val) => { p().transit_layover = val; },
    },
    {
      key: `traveler:${kind}:${index}:same_fare`,
      when: () => index >= 1,
      ask: () => {
        const ref = firstReferenceProfile(data);
        const refLabel = referenceTravelerLabel(data);
        const per = (ref?.cost || 0) + (ref?.profit || 0);
        return `${travelerLabel(kind, index, p())} — same net fare and markup as ${refLabel}? (${fmtAmt(per)} per person)\nReply Y or N.`;
      },
      validate: (v) => (isYes(v) || isNo(v) ? null : 'Reply Y or N.'),
      parse: (v) => isYes(v),
      apply: (val) => {
        const ref = firstReferenceProfile(data);
        if (val && ref) {
          copyFareFields(ref, p());
          p()._sameFareAsFirst = true;
        } else {
          p()._sameFareAsFirst = false;
          p().cost = null;
          p().profit = null;
          p().fare_confirmed = false;
        }
      },
    },
    {
      key: `traveler:${kind}:${index}:cost`,
      ask: `${travelerLabel(kind, index, p())} — net fare (LKR)?`,
      hint: 'Estimated net cost for this passenger',
      when: () => shouldAskFareDetails(kind, index, p()),
      validate: (v) => {
        const n = Number(String(v).trim());
        if (!Number.isFinite(n) || n < 0) return 'Enter amount in LKR (0 or more).';
        return null;
      },
      parse: (v) => Number(String(v).trim()),
      apply: (val) => { p().cost = val; p().fare_confirmed = false; },
    },
    {
      key: `traveler:${kind}:${index}:profit`,
      ask: `${travelerLabel(kind, index, p())} — profit markup (LKR)?`,
      when: () => shouldAskFareDetails(kind, index, p()),
      validate: (v) => {
        const n = Number(String(v).trim());
        if (!Number.isFinite(n) || n < 0) return 'Enter markup in LKR (0 or more).';
        return null;
      },
      parse: (v) => Number(String(v).trim()),
      apply: (val) => { p().profit = val; p().fare_confirmed = false; },
    },
    {
      key: `traveler:${kind}:${index}:fare_confirm`,
      when: () => shouldAskFareDetails(kind, index, p()),
      ask: () => {
        const pr = p();
        const total = (pr.cost || 0) + (pr.profit || 0);
        return `${travelerLabel(kind, index, pr)} total: net ${fmtAmt(pr.cost)} + profit ${fmtAmt(pr.profit)} = ${fmtAmt(total)}\nConfirm this fare? (Y/N)`;
      },
      confirm: true,
      validate: (v) => {
        if (isYes(v) || isNo(v)) return null;
        return 'Reply Y or N.';
      },
      parse: (v) => isYes(v),
      apply: (val) => {
        if (!val) {
          p().cost = null;
          p().profit = null;
          throw new Error('REASK_FARE');
        }
        p().fare_confirmed = true;
      },
    },
  ];
  return steps;
}

function sharedRateSteps(data, kind, count) {
  const tag = kind.toUpperCase();
  return [
    {
      key: `shared:${kind}:cost`,
      ask: `${tag} — net fare per person (LKR)?`,
      validate: (v) => {
        const n = Number(String(v).trim());
        if (!Number.isFinite(n) || n < 0) return 'Enter amount in LKR.';
        return null;
      },
      parse: (v) => Number(String(v).trim()),
      apply: (val) => {
        data.shared_rates[kind] = data.shared_rates[kind] || {};
        data.shared_rates[kind].cost = val;
      },
    },
    {
      key: `shared:${kind}:profit`,
      ask: `${tag} — profit markup per person (LKR)?`,
      validate: (v) => {
        const n = Number(String(v).trim());
        if (!Number.isFinite(n) || n < 0) return 'Enter markup in LKR.';
        return null;
      },
      parse: (v) => Number(String(v).trim()),
      apply: (val) => {
        data.shared_rates[kind] = data.shared_rates[kind] || {};
        data.shared_rates[kind].profit = val;
      },
    },
    {
      key: `shared:${kind}:confirm`,
      ask: () => {
        const r = data.shared_rates[kind] || {};
        const per = (r.cost || 0) + (r.profit || 0);
        const total = per * count;
        return `${count}× ${tag}: ${fmtAmt(per)} each = ${fmtAmt(total)} total\nConfirm? (Y/N)`;
      },
      confirm: true,
      validate: (v) => (isYes(v) || isNo(v) ? null : 'Reply Y or N.'),
      parse: (v) => isYes(v),
      apply: (val) => {
        if (!val) {
          data.shared_rates[kind] = null;
          throw new Error('REASK_SHARED');
        }
        data.shared_rates[kind].confirmed = true;
      },
    },
  ];
}

export function parseDateInput(raw) {
  return parseSmartDate(raw);
}

export function buildNewTicketSteps(data, helpers = {}) {
  const { sources = [], matchSource = (v) => v, formatSourceQuestion = () => 'Lead source?' } = helpers;
  const steps = [
    {
      key: 'name',
      ask: 'Contact / lead name?',
      hint: 'Primary passenger or contact person.',
      validate: (v) => {
        if (!v.trim()) return 'Name is required.';
        if (v.trim().length < 2) return 'Enter at least 2 characters.';
        return null;
      },
      parse: (v) => capitalizeName(v),
      apply: (val) => { data.name = val; },
    },
    {
      key: 'phone',
      ask: 'Mobile number?',
      hint: '+94 77 123 4567 or 0771234567',
      validate: (v) => {
        const phone = helpers.normalizePhone?.(v) || v;
        if (!phone || String(phone).replace(/\D/g, '').length < 9) return 'Enter a valid phone number.';
        return null;
      },
      parse: (v) => (helpers.normalizePhone ? helpers.normalizePhone(v) : v.trim()),
      apply: (val) => { data.phone = val; },
    },
    {
      key: 'email',
      ask: 'Email?',
      hint: 'Skip if none.',
      optional: true,
      validate: (v) => {
        if (isSkip(v)) return null;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return 'Valid email or skip.';
        return null;
      },
      parse: (v) => (isSkip(v) ? null : v.trim()),
      apply: (val) => { data.email = val; },
    },
    {
      key: 'pref_baggage',
      ask: 'Baggage preference?',
      hint: '1 cabin · 2 checked 23kg · 3 checked 30kg+ · 4 extra · skip',
      optional: true,
      parse: (v) => parseBaggage(v),
      apply: (val) => { data.preferences.baggage = val; },
    },
    {
      key: 'pref_seat',
      ask: 'Seat preference?',
      hint: '1 window · 2 aisle · 3 middle · 4 exit · 5 front · skip',
      optional: true,
      parse: (v) => parseSeat(v),
      apply: (val) => { data.preferences.seat = val; },
    },
    {
      key: 'pref_transit',
      ask: 'Flight routing?',
      hint: '1 Direct · 2 Transit · 3 Multi-City',
      validate: (v) => (!parseTransit(v) ? 'Reply 1, 2, or 3 (or direct / transit / multi-city).' : null),
      parse: (v) => parseTransit(v),
      apply: (val) => {
        data.preferences.transit = val;
        if (val !== 'transit') data.preferences.transit_via = null;
        if (val !== 'multi') {
          data.preferences.multi_city_route = null;
          data.routing_segments = null;
        }
        if (val === 'multi') data.trip_type = null;
      },
    },
    {
      key: 'pref_transit_via',
      ask: 'Preferred transit city (IATA)?',
      hint: 'e.g. DOH',
      when: () => data.preferences.transit === 'transit'
        || data.preferences.transit === 'preferred',
      validate: (v) => (/^[A-Z]{3}$/.test(v.trim().toUpperCase()) ? null : 'Enter 3-letter airport code.'),
      parse: (v) => v.trim().toUpperCase(),
      apply: (val) => { data.preferences.transit_via = val; },
    },
    {
      key: 'pref_multi_city',
      ask: 'Multi-city route — airports in order?',
      hint: 'e.g. CMB SIN KUL CMB (min. 3 airports)',
      when: () => data.preferences.transit === 'multi',
      validate: (v) => (!parseMultiCityRoute(v) ? 'Enter airports separated by spaces (e.g. CMB SIN KUL CMB).' : null),
      parse: (v) => parseMultiCityRoute(v),
      apply: (val) => {
        data.preferences.multi_city_route = val.route;
        data.routing_segments = val.segments;
      },
    },
    {
      key: 'pref_trip_type',
      ask: 'Trip type?',
      hint: '1 One way · 2 Return',
      when: () => isSimpleRouting(data),
      validate: (v) => (!parseTripType(v) ? 'Reply 1 or 2 (or one way / return).' : null),
      parse: (v) => parseTripType(v),
      apply: (val) => applyGlobalTripType(data, val),
    },
    {
      key: 'pax_adt',
      ask: 'How many adults (ADT)?',
      hint: '0 if students only',
      validate: (v, data) => {
        const n = Number(String(v).trim());
        if (!Number.isInteger(n) || n < 0) return 'Enter 0 or more.';
        if (n === 0 && !(data?.pax?.stu > 0)) return 'Need at least 1 ADT unless you add students next.';
        return null;
      },
      parse: (v) => Number(String(v).trim()),
      apply: (val) => { data.pax.adt = val; ensureTravelers(data); },
    },
    {
      key: 'pax_stu',
      ask: 'Students (STU)?',
      hint: '0 or skip if none',
      optional: true,
      validate: (v) => {
        if (isSkip(v)) return null;
        const n = Number(String(v).trim());
        if (!Number.isInteger(n) || n < 0) return 'Enter 0 or more.';
        return null;
      },
      parse: (v) => (isSkip(v) ? 0 : Number(String(v).trim())),
      apply: (val) => { data.pax.stu = val; ensureTravelers(data); },
    },
    {
      key: 'pax_chd',
      ask: 'Children (CHD)?',
      optional: true,
      parse: (v) => (isSkip(v) ? 0 : Number(String(v).trim()) || 0),
      apply: (val) => { data.pax.chd = val; },
    },
    {
      key: 'pax_inf',
      ask: 'Infants (INF)?',
      optional: true,
      validate: (v, data) => {
        if (isSkip(v)) return null;
        const n = Number(String(v).trim());
        if (!Number.isInteger(n) || n < 0) return 'Enter 0 or more.';
        const pax = data?.pax || {};
        const total = (pax.adt || 0) + (pax.stu || 0) + (pax.chd || 0) + n;
        if (total < 1) return 'Need at least one passenger (ADT, STU, or CHD).';
        return null;
      },
      parse: (v) => (isSkip(v) ? 0 : Number(String(v).trim()) || 0),
      apply: (val) => { data.pax.inf = val; },
    },
  ];

  ensureTravelers(data);
  for (let i = 0; i < data.pax.adt; i += 1) {
    steps.push(...profileSteps(data, 'adt', i));
  }
  for (let i = 0; i < data.pax.stu; i += 1) {
    steps.push(...profileSteps(data, 'stu', i));
  }
  if (data.pax.chd > 0) steps.push(...sharedRateSteps(data, 'chd', data.pax.chd));
  if (data.pax.inf > 0) steps.push(...sharedRateSteps(data, 'inf', data.pax.inf));

  if (!data._passengersLocked) {
    steps.push({
      key: 'passenger_summary',
      ask: () => `${formatPassengerSummary(data)}\nAdd another passenger? (Y/N)`,
      validate: (v) => (isYes(v) || isNo(v) ? null : 'Reply Y or N.'),
      parse: (v) => isYes(v),
      apply: (val) => {
        if (val) {
          data._addingMore = true;
        } else {
          data._passengersLocked = true;
          data._addingMore = false;
        }
      },
    });
    steps.push({
      key: 'add_passenger_type',
      when: () => data._addingMore,
      ask: 'Passenger type? (ADT / STU / CHD / INF)',
      validate: (v) => (parsePaxType(v) ? null : 'Enter ADT, STU, CHD, or INF.'),
      parse: (v) => parsePaxType(v),
      apply: (kind) => {
        data.pax[kind] = (data.pax[kind] || 0) + 1;
        ensureTravelers(data);
        const idx = data.pax[kind] - 1;
        data._addingMore = false;
        if (kind === 'chd' || kind === 'inf') {
          data.shared_rates[kind] = null;
          data._jumpToKey = `shared:${kind}:cost`;
        } else {
          data._jumpToKey = `traveler:${kind}:${idx}:gender`;
        }
      },
    });
  }

  if (data._passengersLocked) {
    steps.push(
      {
        key: 'source',
        ask: () => formatSourceQuestion(),
        validate: (v) => (!String(v).trim() ? 'Enter lead source.' : null),
        parse: (v) => matchSource(v),
        apply: (val) => { data.source = val; },
      },
      {
        key: 'airline',
        ask: 'Preferred airline?',
        hint: 'Skip if any.',
        optional: true,
        parse: (v) => (isSkip(v) ? null : v.trim()),
        apply: (val) => { data.airline = val; },
      },
      {
        key: 'ticket_class',
        ask: 'Travel class?',
        hint: 'economy / business / first — or skip',
        optional: true,
        parse: (v) => parseClass(v),
        apply: (val) => { data.ticket_class = val; },
      },
      {
        key: 'note',
        ask: 'Notes?',
        hint: 'Skip if none.',
        optional: true,
        parse: (v) => (isSkip(v) ? null : v.trim()),
        apply: (val) => { data.note = val; },
      },
    );
  }

  return steps.filter((step) => !step.when || step.when(data));
}

export function activeNewTicketSteps(data, helpers) {
  return activeNewTicketStepsV2(data, helpers);
}

function storedProfile(pr) {
  return {
    gender: pr.gender || null,
    name: pr.name || null,
    trip_type: normalizeTripType(pr.trip_type) || 'one_way',
    origin: pr.origin || '',
    destination: pr.destination || '',
    depart_date: pr.depart_date || '',
    return_date: pr.return_date || '',
    depart_time: pr.depart_time || '',
    arrive_time: pr.arrive_time || '',
    transit_stop: pr.transit_stop || null,
    transit_layover: pr.transit_layover || null,
    cost: pr.cost ?? null,
    profit: pr.profit ?? null,
    flight_segments: [],
  };
}

export function buildExtraFromTicketData(data) {
  ensureTravelers(data);
  const pax = normalizePaxCounts(data.pax);
  data.pax = pax;

  const extra = {
    pax,
    date_flex: { mode: 'fixed', search_from: null, search_to: null },
    preferences: { ...data.preferences },
    per_adt_itineraries: pax.adt >= 1 || pax.stu > 0,
    adt_profiles: data.travelers.adt.map(storedProfile),
    stu_profiles: pax.stu > 0 ? data.travelers.stu.map(storedProfile) : null,
    adt_pricing: null,
    stu_pricing: null,
    pax_cost_rates: null,
    pax_profit_rates: null,
    pax_rates: null,
  };

  if (data.email) extra.email = data.email;

  if (data.preferences?.transit === 'multi' && data.routing_segments?.length) {
    extra.flight_segments = data.routing_segments.map((s) => ({ ...s }));
    extra.preferences.multi_city_route = data.preferences.multi_city_route || null;
  }

  if (extra.adt_profiles?.length) {
    extra.adt_pricing = extra.adt_profiles.map((p) => ({
      gender: p.gender ?? null,
      name: p.name,
      cost: p.cost ?? null,
      profit: p.profit ?? null,
    }));
  }
  if (extra.stu_profiles?.length) {
    extra.stu_pricing = extra.stu_profiles.map((p) => ({
      gender: p.gender ?? null,
      name: p.name,
      cost: p.cost ?? null,
      profit: p.profit ?? null,
    }));
  }
  if (extra.per_adt_itineraries) {
    const costRates = {};
    const profitRates = {};
    if (pax.chd > 0 && data.shared_rates.chd) {
      costRates.chd = data.shared_rates.chd.cost ?? 0;
      profitRates.chd = data.shared_rates.chd.profit ?? 0;
    }
    if (pax.inf > 0 && data.shared_rates.inf) {
      costRates.inf = data.shared_rates.inf.cost ?? 0;
      profitRates.inf = data.shared_rates.inf.profit ?? 0;
    }
    if (Object.keys(costRates).length) extra.pax_cost_rates = costRates;
    if (Object.keys(profitRates).length) extra.pax_profit_rates = profitRates;
  }

  return mergeCustomFieldsIntoExtra(extra, data._customFields);
}

function applyProfilesToBody(body, data) {
  if (data.preferences?.transit === 'multi' && body.extra?.flight_segments?.length) {
    const segs = body.extra.flight_segments;
    body.trip_type = 'multi';
    body.origin = segs[0]?.origin || null;
    body.destination = segs[segs.length - 1]?.destination || null;
    return;
  }
  const profiles = body.extra?.adt_profiles;
  if (!profiles?.length) {
    const stu = body.extra?.stu_profiles?.[0];
    const src = stu || data.travelers.adt[0];
    if (!src) return;
    const tripType = normalizeTripType(src.trip_type) || 'one_way';
    body.trip_type = tripType;
    body.origin = src.origin || null;
    body.destination = src.destination || null;
    body.depart_date = src.depart_date || null;
    body.return_date = tripType === 'return' ? (src.return_date || null) : null;
    return;
  }
  const first = profiles[0];
  const tripType = normalizeTripType(data.trip_type || first.trip_type || body.trip_type) || 'one_way';
  body.trip_type = tripType;
  body.origin = first.origin || null;
  body.destination = first.destination || null;
  body.depart_date = first.depart_date || null;
  body.return_date = tripType === 'return' ? (first.return_date || null) : null;
}

export function buildNewTicketLeadBody(data, agentId, helpers = {}) {
  return buildNewTicketLeadBodyV2(data, helpers);
}

export function formatNewTicketSummary(data) {
  return formatNewTicketSummaryV2(data);
}

function buildNewTicketLeadBodyLegacy(data, agentId, helpers = {}) {
  const extra = buildExtraFromTicketData(data);
  const breakdown = ticketPricingFromExtra(extra);
  const followup = helpers.defaultFollowupISO?.() || todayISO();

  const body = {
    type: 'ticket',
    name: data.name,
    phone: data.phone,
    source: data.source || 'Call',
    status: 'new',
    agent_id: agentId,
    inquiry_date: todayISO(),
    followup_date: followup,
    origin: null,
    destination: null,
    depart_date: null,
    return_date: null,
    trip_type: 'one_way',
    airline: data.airline || null,
    ticket_class: data.ticket_class || null,
    note_text: data.note || null,
    quote_amount: breakdown?.total > 0 ? breakdown.total : null,
    amount: breakdown?.total > 0 ? breakdown.total : null,
    cost_amount: breakdown?.cost > 0 ? breakdown.cost : null,
    extra: {
      ...extra,
      quoted_total: breakdown?.total > 0 ? breakdown.total : null,
    },
  };

  applyProfilesToBody(body, data);
  return body;
}

function formatNewTicketSummaryLegacy(data) {
  const extra = buildExtraFromTicketData(data);
  const pax = extra.pax || normalizePaxCounts(data.pax);
  const breakdown = ticketPricingFromExtra(extra);
  const lines = [
    '<strong>Ticket inquiry</strong>',
    `Contact: ${esc(data.name)} · ${esc(data.phone)}`,
    `Pax: ${pax.adt} ADT · ${pax.stu} STU · ${pax.chd} CHD · ${pax.inf} INF`,
  ];
  const prefs = data.preferences || {};
  if (prefs.transit === 'multi' && prefs.multi_city_route) {
    lines.push(`Routing: Multi-city · ${esc(prefs.multi_city_route)}`);
  } else if (prefs.transit === 'transit' && prefs.transit_via) {
    lines.push(`Routing: Transit via ${esc(prefs.transit_via)}`);
  } else if (prefs.transit === 'transit' || prefs.transit === 'ok') {
    lines.push('Routing: Transit');
  } else {
    lines.push('Routing: Direct');
  }

  for (let i = 0; i < data.pax.adt; i += 1) {
    const pr = data.travelers.adt[i];
    if (!pr) continue;
    lines.push(`${travelerLabel('adt', i, pr)}: ${pr.origin || '?'}→${pr.destination || '?'} ${fmtDate(pr.depart_date) || ''} · ${fmtAmt((pr.cost || 0) + (pr.profit || 0))}`);
  }
  for (let i = 0; i < data.pax.stu; i += 1) {
    const pr = data.travelers.stu[i];
    if (!pr) continue;
    lines.push(`${travelerLabel('stu', i, pr)}: ${pr.origin || '?'}→${pr.destination || '?'} · ${fmtAmt((pr.cost || 0) + (pr.profit || 0))}`);
  }

  if (breakdown) {
    lines.push(`Quote total: ${fmtAmt(breakdown.total)} (cost ${fmtAmt(breakdown.cost)} + profit ${fmtAmt(breakdown.profit)})`);
  }
  lines.push(`Source: ${esc(data.source || '—')}`);
  if (data.note) lines.push(`Note: ${esc(data.note)}`);
  return lines.join('\n');
}

export function applyNewTicketStep(data, step, text, helpers) {
  const value = step.parse ? step.parse(text) : text.trim();
  try {
    step.apply(value);
  } catch (e) {
    if (e.message === 'REASK_FARE' || e.message === 'REASK_SHARED') {
      return { error: 'Not confirmed — re-enter net fare and markup.' };
    }
    throw e;
  }
  return { ok: true };
}
